import * as React from "react";
import { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { OWNERSHIP_SOURCE } from "../../../utils/franchiseAdminModel";

// Edit Facility Ownership (slice A3) -- one coherent administrative concern: direct BDM,
// PaceSetter, Next Level Leader. A functional port of the shipped Slice 4b modal from
// LocationRow, moved to MUI Dialog per decision #12. The PATCH semantics are unchanged because
// they are already correct and production-proven.
//
// These are all FACILITY-level values. The labels say so explicitly: facility PaceSetter is NOT
// the (separate, unused) franchise-level lksi_pacesetter column, and presenting it as though it
// were would be a real data-integrity trap.

// Selects use string sentinels: '' = null = not configured.
const toSel = (value) => (value == null ? "" : String(value));

// The "not configured" / "inherit" options carry value="", which MUI renders as an EMPTY select
// display -- the label then sits inside the box like a placeholder and an unset field is
// indistinguishable from an unlabelled one. displayEmpty + a pinned (shrunk) label makes the
// sentinel option render as real selected text, so "— Not configured —" is visibly the value.
const EMPTY_SELECT_PROPS = {
  SelectProps: { displayEmpty: true },
  InputLabelProps: { shrink: true },
};

// --- Current ownership summary (multi-BDM slice 3) --------------------------------------------
// READ ONLY. Shows the resolved ownership COLLECTION -- everyone recorded, not just the primary --
// so the dialog stops implying that a facility has exactly one owner. Nothing here is editable and
// nothing here contributes to the save payload: the only writable field remains the scalar
// "Direct facility BDM" select below.

const SOURCE_LABEL = {
  [OWNERSHIP_SOURCE.FACILITY_ASSIGNMENT]: "Direct",
  [OWNERSHIP_SOURCE.FACILITY_SCALAR]: "Direct",
  [OWNERSHIP_SOURCE.FRANCHISE_ASSIGNMENT]: "Inherited from franchise",
  [OWNERSHIP_SOURCE.FRANCHISE_LEGACY]: "Inherited from franchise",
  [OWNERSHIP_SOURCE.NONE]: "Not set",
};

function OwnershipSummary(props) {
  const ownership = props.ownership;
  if (!ownership) return null;

  const isNone = ownership.source === OWNERSHIP_SOURCE.NONE;

  // Every owner, with the primary called out. A collection with no primary shows none rather than
  // guessing -- a visible gap is a prompt to fix the data.
  const ownerLines = ownership.owners.map((owner, index) => (
    <Typography key={owner.bdmId != null ? `id-${owner.bdmId}` : `name-${index}`} variant="body2">
      {owner.name || "—"}
      {owner.isPrimary && (
        <Typography component="span" variant="body2" color="text.secondary">
          {" "}
          (primary)
        </Typography>
      )}
      {owner.active === 0 && (
        <Typography component="span" variant="body2" color="warning.main">
          {" "}
          · inactive
        </Typography>
      )}
    </Typography>
  ));

  // A legacy free-text value that has never been linked to a person in the BDM directory. Worth
  // saying plainly HERE (rather than in the grid) because this is the moment someone is deciding
  // ownership: an unlinked name cannot be picked from the dropdown below, and reports that filter
  // by BDM will not find it.
  const unresolvedNotes = ownership.unresolvedNames.map((name) => (
    <Typography key={`unresolved-${name}`} variant="caption" color="text.secondary">
      ⚠ “{name}” is stored as text and is not linked to a person in the BDM directory.
    </Typography>
  ));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "action.hover",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
          Current ownership
        </Typography>
        <Chip
          label={SOURCE_LABEL[ownership.source] || "Not set"}
          size="small"
          variant={isNone ? "outlined" : "filled"}
          color={ownership.isInherited || isNone ? "default" : "primary"}
        />
      </Box>
      {isNone ? (
        <Typography variant="body2" color="text.disabled">
          No BDM resolves for this facility.
        </Typography>
      ) : (
        ownerLines
      )}
      {unresolvedNotes}
    </Box>
  );
}

export default function EditFacilityOwnershipDialog(props) {
  const open = props.open;
  const onClose = props.onClose;
  const facility = props.facility;
  const bdmOptions = props.bdmOptions || [];
  const onSave = props.onSave;
  const onSaved = props.onSaved;

  const [bdmSel, setBdmSel] = useState("");
  const [paceSel, setPaceSel] = useState("");
  const [nllSel, setNllSel] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed from the CURRENT (possibly just-refetched) values every time the dialog opens, so a
  // reopen never shows stale edits (dialog contract rule 1).
  useEffect(() => {
    if (!open) return;
    setBdmSel(toSel(facility.facilityBdmId));
    setPaceSel(toSel(facility.pacesetter));
    setNllSel(toSel(facility.nextLevelLeader));
    setError(null);
  }, [open, facility]);

  const bdmChanged = bdmSel !== toSel(facility.facilityBdmId);
  const paceChanged = paceSel !== toSel(facility.pacesetter);
  const nllChanged = nllSel !== toSel(facility.nextLevelLeader);
  const hasChanges = bdmChanged || paceChanged || nllChanged;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Send ONLY what changed. Leaving bdm_id untouched (e.g. when it points at a since-
      // deactivated BDM) omits it entirely, so the server never re-validates and never returns
      // the inactive-assignment 409 for an edit that had nothing to do with the BDM.
      const payload = { dim_facilityID: facility.id };
      if (bdmChanged) payload.bdm_id = bdmSel === "" ? null : Number(bdmSel);
      if (paceChanged) payload.pacesetter = paceSel === "" ? null : Number(paceSel);
      if (nllChanged) payload.next_level_leader = nllSel === "" ? null : Number(nllSel);

      await onSave(payload);
      if (onSaved) onSaved(`${facility.name} ownership updated`);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Only ACTIVE BDMs are selectable. An existing assignment to an inactive BDM is still shown --
  // disabled -- so the current state is visible and can be cleared, but not re-selected.
  const activeBdmItems = bdmOptions
    .filter((bdm) => bdm.active)
    .map((bdm) => (
      <MenuItem key={bdm.bdm_id} value={String(bdm.bdm_id)}>
        {bdm.display_name}
      </MenuItem>
    ));

  const inactiveCurrentItem =
    facility.facilityBdmId != null && facility.facilityBdmActive === 0 ? (
      <MenuItem key={`inactive-${facility.facilityBdmId}`} value={String(facility.facilityBdmId)} disabled>
        {(facility.facilityBdmName || "Unknown") + " (inactive)"}
      </MenuItem>
    ) : null;

  // Live preview of what the effective BDM will be after saving.
  const selectedBdm = bdmOptions.find((bdm) => String(bdm.bdm_id) === bdmSel);
  const selectedBdmName = selectedBdm
    ? selectedBdm.display_name
    : bdmSel === toSel(facility.facilityBdmId)
    ? facility.facilityBdmName
    : null;
  const previewInherited = bdmSel === "";
  const previewEffective = previewInherited ? facility.franchiseBdmName : selectedBdmName;

  const ownership = facility.ownership;

  // GUARD (multi-BDM slice 3). This facility's own ownership is a COLLECTION of assignments, which
  // a single-select scalar cannot express. Saving through it would silently drop every owner but
  // one, so the field is locked until the slice 4 assignment editor exists. PaceSetter and Next
  // Level Leader stay editable -- the lock is scoped to the field that cannot represent the truth.
  const lockedCollection = Boolean(ownership && ownership.isUneditableCollection);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{facility.name} — Facility Ownership</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <OwnershipSummary ownership={ownership} />

          <Typography variant="body2" color="text.secondary">
            Franchise default BDM: <strong>{facility.franchiseBdmName || "—"}</strong>
          </Typography>

          {lockedCollection && (
            <Alert severity="info">
              This facility has {ownership.ownerCount} assigned BDMs. Editing a single direct BDM
              here would replace all of them, so the field is locked until shared ownership can be
              edited directly.
            </Alert>
          )}

          <TextField
            select
            label="Direct facility BDM"
            size="small"
            fullWidth
            {...EMPTY_SELECT_PROPS}
            value={bdmSel}
            onChange={(event) => setBdmSel(event.target.value)}
            disabled={saving || lockedCollection}
            helperText={
              lockedCollection
                ? "Locked — shared ownership cannot be edited from this field"
                : previewInherited
                ? `Inheriting the franchise default (${facility.franchiseBdmName || "none"})`
                : "Overrides the franchise default for this facility"
            }
          >
            <MenuItem value="">— Inherit franchise default —</MenuItem>
            {inactiveCurrentItem}
            {activeBdmItems}
          </TextField>

          {!lockedCollection && (
            <Typography variant="body2">
              Effective BDM: <strong>{previewEffective || "—"}</strong>{" "}
              <Typography component="em" variant="body2" color="text.secondary">
                ({previewInherited ? "inherited" : "direct"})
              </Typography>
            </Typography>
          )}

          <TextField
            select
            label="Facility PaceSetter"
            size="small"
            fullWidth
            {...EMPTY_SELECT_PROPS}
            value={paceSel}
            onChange={(event) => setPaceSel(event.target.value)}
            disabled={saving}
          >
            <MenuItem value="">— Not configured —</MenuItem>
            <MenuItem value="1">Yes</MenuItem>
            <MenuItem value="0">No</MenuItem>
          </TextField>

          <TextField
            select
            label="Facility Next Level Leader"
            size="small"
            fullWidth
            {...EMPTY_SELECT_PROPS}
            value={nllSel}
            onChange={(event) => setNllSel(event.target.value)}
            disabled={saving}
          >
            <MenuItem value="">— Not configured —</MenuItem>
            <MenuItem value="1">Yes</MenuItem>
            <MenuItem value="0">No</MenuItem>
          </TextField>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || !hasChanges} variant="contained">
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
